import React, { useState } from "react";
import { X, Check, Share2, Link2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { CiFacebook } from "react-icons/ci";
import { CiTwitter } from "react-icons/ci";
import { CiLinkedin } from "react-icons/ci";



export default function ShareModal({ open, setOpen, url }) {
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const shareUrl = encodeURIComponent(url);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const handleClose = () => {
    setOpen(false);
    setCopied(false);
  };

  const shareOptions = [
    {
      name: "WhatsApp",
      icon: <FaWhatsapp/>,
      color: "bg-[#25D366] hover:bg-[#20b859]",
      href: `https://wa.me/?text=${shareUrl}`,
    },
    {
      name: "Facebook",
      icon: <CiFacebook/>,
      color: "bg-[#1877F2] hover:bg-[#0e63d6]",
      href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    },
    {
      name: "Twitter",
      icon: <CiTwitter/>,
      color: "bg-[#1DA1F2] hover:bg-[#1a8cd8]",
      href: `https://twitter.com/intent/tweet?url=${shareUrl}`,
    },
    {
      name: "LinkedIn",
      icon: <CiLinkedin/>,
      color: "bg-[#0A66C2] hover:bg-[#0957a8]",
      href: `https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}`,
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all duration-300">
      <div className="bg-white rounded-2xl w-full max-w-md transform transition-all duration-300 scale-100 animate-in fade-in zoom-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-blue-100">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-50 rounded-lg">
              <Share2 className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Share this content</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors duration-200 group"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* URL Preview */}
          <div className="mb-6 p-3 bg-blue-50/50 rounded-xl border border-blue-100">
            <p className="text-xs text-blue-600 font-medium mb-1">Link to share</p>
            <p className="text-sm text-gray-600 truncate">{url}</p>
          </div>

          {/* Share Options */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {shareOptions.map((option) => (
              <a
                key={option.name}
                href={option.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className={`${option.color} text-white py-2.5 rounded-xl text-center font-medium transition-all duration-200 hover:shadow-md hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2`}
              >
                <span className="text-lg">{option.icon}</span>
                <span>{option.name}</span>
              </a>
            ))}
          </div>

          {/* Copy Link Button */}
          <button
            onClick={handleCopy}
            className={`w-full py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              copied
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
            }`}
          >
            {copied ? (
              <>
                <Check className="w-5 h-5" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Link2 className="w-5 h-5" />
                <span>Copy Link</span>
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 bg-blue-50/30 rounded-b-2xl border-t border-blue-100">
          <p className="text-xs text-center text-blue-600">
            Share with your friends and colleagues
          </p>
        </div>
      </div>
    </div>
  );
}